import { computed, ref, watch } from 'vue';
import SalesTransactionReceiptAPI from 'dashboard/api/salesTransactionReceipt';

const MAX_RECEIPT_BYTES = 50 * 1024 * 1024;
const RECEIPT_CONTENT_TYPES = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]);
const RECEIPT_FILE_NAMES = /\.(pdf|docx?)$/i;

const errorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.response?.data?.error || fallback;

export function useSalesReceipt({ id, t, notify, receipt }) {
  const data = ref(receipt?.value || null);
  const loading = ref(false);
  const uploading = ref(false);
  const removing = ref(false);

  const document = computed(() => data.value?.document || null);
  const canUpload = computed(() =>
    Boolean(data.value?.permissions?.can_upload)
  );
  const canRemove = computed(() =>
    Boolean(data.value?.permissions?.can_delete)
  );

  watch(
    receipt,
    value => {
      data.value = value || null;
    },
    { immediate: true }
  );

  const load = async () => {
    if (!id.value) return null;
    loading.value = true;
    try {
      const response = await SalesTransactionReceiptAPI.show(id.value);
      data.value = response?.data?.data || null;
      return data.value;
    } catch (error) {
      notify(errorMessage(error, t('SALES.DETAIL.RECEIPT.ERROR_LOAD')));
      return null;
    } finally {
      loading.value = false;
    }
  };

  const upload = async file => {
    if (!id.value || !file) return null;
    if (
      !RECEIPT_CONTENT_TYPES.has(file.type) &&
      !RECEIPT_FILE_NAMES.test(file.name || '')
    ) {
      notify(t('SALES.DETAIL.RECEIPT.FORMAT_ERROR'));
      return null;
    }
    if (!file.size || file.size > MAX_RECEIPT_BYTES) {
      notify(t('SALES.DETAIL.RECEIPT.SIZE_ERROR'));
      return null;
    }
    uploading.value = true;
    try {
      const response = await SalesTransactionReceiptAPI.upload(id.value, file);
      data.value = response?.data?.data || data.value;
      notify(t('SALES.DETAIL.RECEIPT.UPLOAD_SUCCESS'));
      return data.value;
    } catch (error) {
      notify(errorMessage(error, t('SALES.DETAIL.RECEIPT.ERROR_UPLOAD')));
      return null;
    } finally {
      uploading.value = false;
    }
  };

  const download = async () => {
    if (!id.value || !document.value?.download_available) return null;
    try {
      const response = await SalesTransactionReceiptAPI.signedUrl(id.value);
      const url = response?.data?.data?.url;
      if (url) window.open(url, '_blank', 'noopener');
      return url;
    } catch (error) {
      notify(errorMessage(error, t('SALES.DETAIL.RECEIPT.ERROR_DOWNLOAD')));
      return null;
    }
  };

  const remove = async () => {
    if (!id.value || !document.value || !canRemove.value) return false;
    removing.value = true;
    try {
      await SalesTransactionReceiptAPI.remove(id.value);
      data.value = { ...data.value, document: null };
      notify(t('SALES.DETAIL.RECEIPT.REMOVE_SUCCESS'));
      return true;
    } catch (error) {
      notify(errorMessage(error, t('SALES.DETAIL.RECEIPT.ERROR_REMOVE')));
      return false;
    } finally {
      removing.value = false;
    }
  };

  return {
    canRemove,
    canUpload,
    data,
    document,
    download,
    load,
    loading,
    remove,
    removing,
    upload,
    uploading,
  };
}
